import React, { useEffect, useState } from "react";
import { Container, Row, Col, Image, Button } from "react-bootstrap";
import Header from "../../../header/Header";
import Footer from "../../../footer/Footer";
import Loader from "../../../loader/Loader";
import Scenario1 from "../../../../assets/images/index/Scenario1.jpg";
import Scenario2 from "../../../../assets/images/index/Scenario2.jpg";
import Scenario3 from "../../../../assets/images/index/Scenario3.jpg";
import Scenario4 from "../../../../assets/images/index/Scenario4.jpg";
import Scenario5 from "../../../../assets/images/index/Scenario5.jpg";
import Scenario6 from "../../../../assets/images/index/Scenario6.jpg";

import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination, Mousewheel } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "../../home/swiper-custom.css";

const UserStories = () => {
  const [loading, setloading] = useState(true);
  const [swiperDirection, setSwiperDirection] = useState("vertical");

  useEffect(() => {
    document.title = "Support Scenarios | Elderly Life";
    setTimeout(() => {
      setloading(false);
    }, 500);
  }, []);

  useEffect(() => {
    const updateDirection = () => {
      const isMobile = window.innerWidth < 992;
      setSwiperDirection(isMobile ? "horizontal" : "vertical");
    };
    updateDirection();
    window.addEventListener("resize", updateDirection);
    return () => window.removeEventListener("resize", updateDirection);
  }, []);

  const scenarios = [
    {
      subtitleLines: ["Scenario 1", "Supporting Mum", "From Another Home"],
      title: "Family Support Across Different Households",
      description: "Sarah lives two hours away from her mum, but still wants to help her stay organised with everyday life. With Elderly Life, they can manage reminders, important tasks and life admin together, helping nothing important get missed.",
      bullets: [
        "Shared reminders for important dates and renewals",
        "Everyday organisation without taking independence away",
        "Quiet support across different households",
        "Support doesn’t have to live under the same roof."
      ],
      image: Scenario1
    },
    {
      subtitleLines: ["Scenario 2", "Staying Organised", "As A Couple"],
      title: "Shared Support For Everyday Life",
      description: "Many couples use Elderly Life together to stay on top of reminders, paperwork, subscriptions and everyday organisation. Support works both ways, helping each person feel more organised, prepared and connected.",
      bullets: [
        "Shared reminders and household organisation",
        "Support each other with everyday tasks",
        "Stay connected and organised together",
        "Because support can be shared equally."
      ],
      image: Scenario2
    },
    {
      subtitleLines: ["Scenario 3", "Quiet Support", "For Busy Families"],
      title: "Family Support Made Simpler",
      description: "From sons and daughters to siblings and extended family members, Elderly Life helps families quietly support one another without taking independence away. Because sometimes the smallest reminders make the biggest difference.",
      bullets: [
        "Shared reminders and important dates",
        "Support from multiple trusted family members",
        "Help staying organised together",
        "Support that fits around everyday family life."
      ],
      image: Scenario3
    },
    {
      subtitleLines: ["Scenario 4", "Support Across", "Different Households"],
      title: "Connected Support Wherever Family Lives",
      description: "Families don’t always live nearby, but support can still stay connected. Elderly Life helps people stay organised together across different homes, cities and routines.",
      bullets: [
        "Stay connected from different locations",
        "Help organise reminders and important tasks",
        "Support loved ones without needing to live together",
        "Because support can happen from anywhere."
      ],
      image: Scenario4
    },
    {
      subtitleLines: ["Scenario 5", "Support", "For Carers"],
      title: "Collaborative Everyday Support",
      description: "Professional carers and support workers can use Elderly Life to help older adults stay organised with reminders, important dates and everyday life tasks. Helping support stay calm, collaborative and organised.",
      bullets: [
        "Support everyday organisation",
        "Reduce missed reminders and important tasks",
        "Help older adults stay independent and informed",
        "Support that works alongside people, not over them."
      ],
      image: Scenario5
    },
    {
      subtitleLines: ["Scenario 6", "Helping Someone", "Stay Independent"],
      title: "Calm Support For Everyday Life",
      description: "Elderly Life is designed to help people stay organised, connected and more confident in everyday life. Because growing older shouldn’t mean losing independence.",
      bullets: [
        "Stay on top of reminders and life admin",
        "Feel more organised and reassured",
        "Keep trusted support nearby when needed",
        "Helping life feel calmer and easier to manage."
      ],
      image: Scenario6
    }
  ];

  return (
    <>
      <Header />
      {loading ? (
        <Loader />
      ) : (
        <>
          <div className="hero">
            <Container>
              <Row className="justify-content-between gx-xxl-0 align-items-stretch">
                <Col xl="13" xxl="13" xxxl="12" className="order-2 order-xl-1">
                  <h1 className="h2 mb-5 mb-md-6 pt-xl-10 pt-xxxl-11">
                    Support Looks Different For Every Family
                  </h1>
                  
                  <div className="d-block d-xl-none mt-4 mb-5">
                    <div className="w-100 rounded-4 shadow-sm border overflow-hidden" style={{ position: "relative", paddingTop: "56.25%", height: 0 }}>
                      <iframe
                        src="https://player.vimeo.com/video/1204543919?title=0&byline=0&portrait=0&badge=0&autopause=0"
                        frameBorder="0"
                        allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                        style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
                        title="Support Scenario"
                      ></iframe>
                    </div>
                  </div>

                  <div className="lead text-dark mb-5" style={{ lineHeight: 1.7 }}>
                    <p className="mb-2">Every family's support journey is different.</p>
                    <p className="mb-2">Some people want help staying organised.</p>
                    <p className="mb-2">Some couples support one another together.</p>
                    <p className="mb-2">Some families quietly help from another household.</p>
                    <p className="mb-0">Others simply want reassurance that nothing important gets missed.</p>
                  </div>

                  <p className="lead mb-4 text-dark">
                    Elderly Life helps individuals, couples, families and carers stay connected, organised and supported in a way that feels calm, collaborative and human.
                  </p>

                  <p className="lead mb-4 text-dark">
                    From reminders and paperwork to shared everyday support, Elderly Life is designed to help people stay independent, while knowing trusted support is there when needed.
                  </p>

                  <p className="lead mb-7 text-dark fw-bold">
                    Support doesn't have to mean taking over.<br />
                    It can simply mean being there alongside someone.
                  </p>

                  <Button
                    href="/plans"
                    size="lg"
                  >
                    Start Your Free Month
                  </Button>
                </Col>
                 <Col xl="11" xxl="11" xxxl="12" className="order-1 order-xl-2 d-none d-xl-block">
                  <div style={{ position: "sticky", top: "7rem", marginTop: "5rem" }}>
                    <div className="w-100 rounded-4 shadow-sm border overflow-hidden" style={{ position: "relative", paddingTop: "56.25%", height: 0 }}>
                      <iframe
                        src="https://player.vimeo.com/video/1204543919?title=0&byline=0&portrait=0&badge=0&autopause=0"
                        frameBorder="0"
                        allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                        style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
                        title="Support Scenario"
                      ></iframe>
                    </div>
                  </div>
                </Col>
              </Row>
            </Container>
          </div>
          <section className="pb-11 pb-md-12 pt-xl-1 pb-xl-13 pt-xxxl-11 pb-xxxl-14">
            <Container>
              <Row className="justify-content-center mb-10 mb-md-11 mb-xl-12">
                <Col xl="20" xxxl="17">
                  <h2 className="text-center mb-5 mb-md-6">
                    How Elderly Life Makes a Difference
                  </h2>
                </Col>
              </Row>
              <Row className="justify-content-center">
                <Col xxl="24" xl="24" lg="24">
                  <Swiper
                    modules={[Autoplay, Pagination, Mousewheel]}
                    slidesPerView={1}
                    centeredSlides={false}
                    loop={true}
                    spaceBetween={0}
                    pagination={{ clickable: true }}
                    autoplay={{
                      delay: 10000,
                      reverseDirection: false,
                      disableOnInteraction: false
                    }}
                    speed={1000}
                    direction={swiperDirection}
                    paginationDirection="horizontal"
                    mousewheel={{ releaseOnEdges: true }}
                    className="custom-swiper scenarios-swiper rounded-4"
                    style={{
                      background: "linear-gradient(145deg, #e1fdf7 0%, #ffffff 100%)",
                      boxShadow: "0 10px 30px rgba(54, 241, 204, 0.1)",
                      border: "1px solid #ccebe5"
                    }}
                  >
                    {scenarios.map((scenario, index) => (
                      <SwiperSlide key={index}>
                        <div className="swiper-slide-content d-flex flex-column flex-md-row gap-5 align-items-center">
                          <div className="w-100 w-md-50">
                            <Image
                              src={scenario.image}
                              className="scenario-img img-fluid rounded-3 shadow-sm"
                              alt={scenario.title}
                            />
                          </div>
                          <div className="w-100 w-md-50 m-md-auto text-start">
                            <div className="mb-3 text-start">
                              <span className="d-block text-uppercase fw-bold mb-1" style={{ color: "#38b79c", fontSize: "0.75rem", letterSpacing: "0.1rem" }}>
                                {scenario.subtitleLines[0]}
                              </span>
                              <span className="d-block fw-semibold text-muted text-uppercase" style={{ fontSize: "0.85rem", letterSpacing: "0.05rem", lineHeight: "1.3" }}>
                                {scenario.subtitleLines.slice(1).join(" ")}
                              </span>
                            </div>
                            <h3 className="fw-bold mb-4 h4" style={{ color: "#003a6f" }}>
                              {scenario.title}
                            </h3>
                            <p className="text-dark mb-4 lead" style={{ fontSize: "1rem", lineHeight: "1.6" }}>
                              {scenario.description}
                            </p>
                            <h6 className="fw-bold mb-3 text-dark">
                              How Elderly Life Helps
                            </h6>
                            <ul className="list-unstyled text-dark mb-0">
                              {scenario.bullets.map((bullet, i) => (
                                <li key={i} className="mb-2 d-flex align-items-start" style={{ fontSize: "0.92rem" }}>
                                  <span className="text-primary me-2">•</span>
                                  <span>{bullet}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </Col>
              </Row>
            </Container>
          </section>

          <Footer />
        </>
      )}
    </>
  );
};

export default UserStories;
